import { clamp, smoothstep } from '../core/math';
import type { Vehicle } from '../vehicles/types';
import type { VehicleService } from '../vehicles/vehicleSystem';
import type { Mixer } from './mixer';
import { distanceCutoff, distanceGain, panFor, radialVelocityAway, type Listener } from './spatial';
import { softClipCurve } from './synth';

export { distanceGain } from './spatial';

/**
 * Original period two-tone "nee-naw" (not a copy of any real siren): two pitches 640/490 Hz (about a fourth) swapping
 * every 0.55 s with a 70 ms glide, band-limited saw + square through a horn-ish bandpass and an oversampled soft clip,
 * slow tremolo. Doppler uses an exaggerated speed of sound so pursuits audibly swoop.
 */

export const SIREN_PERIOD = 1.1;
export const SIREN_HALF = SIREN_PERIOD / 2;
export const SIREN_HI = 640;
export const SIREN_LO = 490;
export const SIREN_GLIDE = 0.07;
/** Exaggerated speed of sound (m/s) for Doppler. */
export const DOPPLER_C = 120;

/** Pitch ratio heard for a source moving apart (+) / together (-) at `radialVelocityAway` m/s. */
export function dopplerRatio(radialVelocityAway: number, c = DOPPLER_C): number {
  const v = clamp(radialVelocityAway, -0.8 * c, 3 * c);
  return c / (c + v);
}

export function ratioToCents(ratio: number): number {
  return 1200 * Math.log2(ratio);
}

/** Siren pitch (Hz) at pattern time `t` seconds, with a per-car `phase` offset. */
export function sirenFreqAt(t: number, phase = 0): number {
  let u = ((t + phase) / SIREN_PERIOD) % 1;
  if (u < 0) u += 1;
  const g = SIREN_GLIDE / SIREN_PERIOD;
  if (u < g) return SIREN_LO + (SIREN_HI - SIREN_LO) * smoothstep(0, 1, u / g);
  if (u < 0.5) return SIREN_HI;
  if (u < 0.5 + g) return SIREN_HI + (SIREN_LO - SIREN_HI) * smoothstep(0, 1, (u - 0.5) / g);
  return SIREN_LO;
}

/** Deterministic per-vehicle phase so cars desync. */
export function sirenPhase(vehicleId: number): number {
  return (vehicleId * 0.2371) % SIREN_PERIOD;
}

const POOL_SIZE = 3;
const RANGE = 160;
const RANGE_SQ = RANGE * RANGE;
const SCAN_INTERVAL = 0.2;
const RELEASE_TIME = 0.35;
const HORIZON = 0.5;

interface Nodes {
  out: GainNode;
  pan: StereoPannerNode;
  dist: BiquadFilterNode;
  pitch: ConstantSourceNode;
  saw: OscillatorNode;
  square: OscillatorNode;
  all: AudioNode[];
}

let curve: Float32Array<ArrayBuffer> | null = null;

function buildNodes(mx: Mixer): Nodes {
  const ctx = mx.ctx as AudioContext;
  const t = ctx.currentTime;
  const all: AudioNode[] = [];
  const reg = <T extends AudioNode>(n: T): T => {
    all.push(n);
    return n;
  };
  const out = reg(ctx.createGain());
  out.gain.value = 0;
  const pan = reg(ctx.createStereoPanner());
  const dist = reg(ctx.createBiquadFilter());
  dist.type = 'lowpass';
  dist.frequency.value = 12000;
  dist.Q.value = 0.5;
  const trem = reg(ctx.createGain());
  trem.gain.value = 0.9;
  const shaper = reg(ctx.createWaveShaper());
  curve ??= softClipCurve(2.2);
  shaper.curve = curve;
  shaper.oversample = '4x';
  const horn = reg(ctx.createBiquadFilter());
  horn.type = 'bandpass';
  horn.frequency.value = 1100;
  horn.Q.value = 0.8;
  const mix = reg(ctx.createGain());
  mix.gain.value = 1;

  const pitch = reg(ctx.createConstantSource());
  pitch.offset.value = SIREN_LO;
  const saw = reg(ctx.createOscillator());
  saw.setPeriodicWave(mx.sawLimited);
  saw.frequency.value = 0;
  const square = reg(ctx.createOscillator());
  square.type = 'square';
  square.frequency.value = 0;
  square.detune.value = 4;
  pitch.connect(saw.frequency);
  pitch.connect(square.frequency);
  const gSaw = reg(ctx.createGain());
  gSaw.gain.value = 0.6;
  const gSq = reg(ctx.createGain());
  gSq.gain.value = 0.22;
  saw.connect(gSaw).connect(mix);
  square.connect(gSq).connect(mix);
  mix.connect(horn).connect(shaper).connect(trem).connect(dist).connect(pan).connect(out);

  const lfo = reg(ctx.createOscillator());
  lfo.frequency.value = 5.3;
  const lfoDepth = reg(ctx.createGain());
  lfoDepth.gain.value = 0.1;
  lfo.connect(lfoDepth).connect(trem.gain);

  pitch.start(t);
  saw.start(t);
  square.start(t);
  lfo.start(t);
  return { out, pan, dist, pitch, saw, square, all };
}

type VoiceState = 'free' | 'active' | 'releasing';

class SirenVoice {
  vehicle: Vehicle | null = null;
  state: VoiceState = 'free';
  phase = 0;
  /** Next pitch-swap boundary index (even = lo->hi, odd = hi->lo) and its audio time. */
  nextN = 0;
  nextAt = 0;
  level = 0;
  ratio = 1;
  releaseLeft = 0;
  fresh = false;
  connected = false;
  nodes: Nodes | null = null;
}

export class SirenPool {
  private readonly voices: SirenVoice[] = [];
  private scan = 0;
  private readonly candV: (Vehicle | null)[] = new Array<Vehicle | null>(POOL_SIZE).fill(null);
  private readonly candD: number[] = new Array<number>(POOL_SIZE).fill(Infinity);

  constructor(
    private readonly mx: Mixer,
    private readonly listener: Listener,
  ) {
    for (let i = 0; i < POOL_SIZE; i++) this.voices.push(new SirenVoice());
  }

  get activeCount(): number {
    let n = 0;
    for (const v of this.voices) if (v.state === 'active') n++;
    return n;
  }

  /** The loudest active siren's voice level (0..0.55), for ducking the radio and ambience under it. */
  get loudest(): number {
    let m = 0;
    for (const v of this.voices) if (v.state === 'active' && v.level > m) m = v.level;
    return m;
  }

  update(dt: number, vehicles: VehicleService | undefined): void {
    if (!vehicles) return;
    this.scan -= dt;
    if (this.scan <= 0) {
      this.scan = SCAN_INTERVAL;
      this.reassign(vehicles);
    }
    const live = this.mx.running && this.mx.ready;
    const now = this.mx.now();
    for (const v of this.voices) {
      if (v.state === 'free') continue;
      if (v.state === 'releasing') {
        v.releaseLeft -= dt;
        if (live && v.nodes) v.nodes.out.gain.setTargetAtTime(0, now, 0.07);
        if (v.releaseLeft <= 0) this.free(v);
        continue;
      }
      const veh = v.vehicle;
      if (!veh || vehicles.get(veh.id) !== veh || veh.destroyed || !veh.siren) {
        this.release(v);
        continue;
      }
      try {
        this.drive(v, veh, live, now);
      } catch (e) {
        this.release(v);
        if (typeof console !== 'undefined') console.warn('[audio] siren voice failed', e);
      }
    }
  }

  private drive(v: SirenVoice, veh: Vehicle, live: boolean, now: number): void {
    const l = this.listener;
    const dx = veh.pos.x - l.x;
    const dz = veh.pos.z - l.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    v.level = distanceGain(d, 40) * 0.55;
    v.ratio = dopplerRatio(radialVelocityAway(dx, dz, veh.velocity.x, veh.velocity.z, l.vx, l.vz));
    if (!live) return;
    const n = v.nodes ?? (v.nodes = buildNodes(this.mx));
    if (!v.connected) {
      n.out.connect(this.mx.buses.siren);
      v.connected = true;
    }
    const f = n.pitch.offset;
    if (v.fresh) {
      const pt = now + v.phase;
      v.nextN = Math.ceil(pt / SIREN_HALF);
      v.nextAt = v.nextN * SIREN_HALF - v.phase;
      f.cancelScheduledValues(now);
      f.setValueAtTime(sirenFreqAt(now, v.phase), now);
      n.out.gain.cancelScheduledValues(now);
      n.out.gain.setValueAtTime(0, now);
      v.fresh = false;
    }
    // Keep the pitch automation scheduled a little ahead of the clock.
    while (v.nextAt < now + HORIZON) {
      const up = (v.nextN & 1) === 0;
      const from = up ? SIREN_LO : SIREN_HI;
      const to = up ? SIREN_HI : SIREN_LO;
      if (v.nextAt >= now - 0.01) {
        f.setValueAtTime(from, v.nextAt);
        f.linearRampToValueAtTime(to, v.nextAt + SIREN_GLIDE);
      }
      v.nextN++;
      v.nextAt += SIREN_HALF;
    }
    const cents = ratioToCents(v.ratio);
    n.saw.detune.setTargetAtTime(cents, now, 0.08);
    n.square.detune.setTargetAtTime(cents + 4, now, 0.08);
    n.dist.frequency.setTargetAtTime(Math.min(12000, distanceCutoff(d)), now, 0.1);
    n.pan.pan.setTargetAtTime(panFor(dx, dz, l.rx, l.rz), now, 0.08);
    n.out.gain.setTargetAtTime(v.level, now, 0.06);
  }

  private reassign(vehicles: VehicleService): void {
    const l = this.listener;
    const cv = this.candV;
    const cd = this.candD;
    for (let i = 0; i < POOL_SIZE; i++) {
      cv[i] = null;
      cd[i] = Infinity;
    }
    for (const veh of vehicles.all()) {
      if (!veh.isPolice || !veh.siren || veh.destroyed) continue;
      const dx = veh.pos.x - l.x;
      const dz = veh.pos.z - l.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > RANGE_SQ || d2 >= (cd[POOL_SIZE - 1] as number)) continue;
      let i = POOL_SIZE - 1;
      while (i > 0 && d2 < (cd[i - 1] as number)) {
        cd[i] = cd[i - 1] as number;
        cv[i] = cv[i - 1] as Vehicle | null;
        i--;
      }
      cd[i] = d2;
      cv[i] = veh;
    }
    for (const v of this.voices) {
      if (v.state !== 'active') continue;
      if (!cv.includes(v.vehicle)) this.release(v);
    }
    for (let i = 0; i < POOL_SIZE; i++) {
      const veh = cv[i];
      if (!veh) continue;
      if (this.voices.some((v) => v.state === 'active' && v.vehicle === veh)) continue;
      const free = this.voices.find((v) => v.state === 'free');
      if (!free) break;
      free.vehicle = veh;
      free.state = 'active';
      free.phase = sirenPhase(veh.id);
      free.fresh = true;
      free.level = 0;
    }
  }

  private release(v: SirenVoice): void {
    v.state = 'releasing';
    v.releaseLeft = RELEASE_TIME;
  }

  private free(v: SirenVoice): void {
    if (v.nodes && v.connected) {
      v.nodes.out.disconnect();
      v.connected = false;
    }
    v.state = 'free';
    v.vehicle = null;
  }

  dispose(): void {
    for (const v of this.voices) {
      const n = v.nodes;
      if (!n) continue;
      for (const s of [n.pitch, n.saw, n.square]) {
        try {
          s.stop();
        } catch {
          /* not started */
        }
      }
      for (const node of n.all) node.disconnect();
      v.nodes = null;
    }
  }
}
